import { Form, Formik } from "formik";
import React from "react";
import { useToast } from "../../context/ToastContext";
import { number, object, string } from "yup";
import { Endpoint } from "../../api/base";
import {
  fieldResourceTypes,
  coverageScopeOptions,
  generateQuickGuid,
  getPhoneMask,
} from "../../components/shared/helpers/helper";
import Select from "react-select";
import {
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  styled,
} from "@mui/material";
import { PrimaryButton } from "../../components/shared/helpers/GlobalStylesv5";
import { states } from "../../components/shared/helpers/states";
import InputMask from "react-input-mask";
import { tokens } from "../../theme/tokens";
import {
  geocodeQuery,
  hasCoords,
} from "../../components/FieldResourceView/geocode";

const FormContainer = styled(Box)({
  padding: "16px",
  overflowY: "auto",
  flex: 1,
});

const FieldRow = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "12px",
  marginBottom: "12px",
});

const FieldGroup = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const FieldLabel = styled(Typography)({
  fontSize: tokens.type.caption.size,
  fontWeight: 500,
  color: tokens.color.text.secondary,
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    height: "36px",
    fontSize: "0.875rem",
    backgroundColor: tokens.color.surface.sunken,
    borderRadius: tokens.radius.sm,
  },
  "& .MuiOutlinedInput-input": {
    padding: "8px 12px",
  },
});

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "36px",
    fontSize: "0.875rem",
    backgroundColor: tokens.color.surface.sunken,
    borderRadius: tokens.radius.sm,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
};

const SectionTitle = styled(Typography)({
  fontSize: tokens.type.dense.size,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: tokens.color.text.secondary,
  marginTop: "20px",
  marginBottom: "12px",
  paddingBottom: "6px",
  borderBottom: `1px solid ${tokens.color.border.default}`,
});

function AddFieldResource({ initState, onClose }) {
  const { showToast } = useToast();

  const handleSubmit = async (values, setSubmitting) => {
    const v = { ...values };

    // The map plots resources by lat/lng, which the form doesn't expose, so
    // derive them from the address whenever it changes or they're missing.
    const addressChanged =
      String(v.address || "").trim() !== String(initState.address || "").trim();
    if (addressChanged || !hasCoords(v.lat, v.lng)) {
      const geo = await geocodeQuery(v.address);
      if (!geo) {
        showToast(
          "Couldn't locate that address. Check it and try again.",
          "error",
        );
        setSubmitting(false);
        return;
      }
      v.lat = geo.lat;
      v.lng = geo.lng;
    }

    if (v.id === "") {
      v.id = generateQuickGuid("client");
    }

    Endpoint.createFieldResource(v)
      .then((res) => {
        showToast("Field Resource saved successfully.", "success");
        window.location.reload();
      })
      .catch((err) => {
        showToast("Sorry something went wrong, please try again later.", "error");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const validateSchema = object().shape({
    name: string().required("Name is required"),
    email: string().required("Email is required"),
    rate: number().when("type", {
      is: "processor",
      then: number().moreThan(0, "Rate must be greater than 0"),
      otherwise: number().notRequired(),
    }),
    hourly_rate: number().when("type", {
      is: "investigator",
      then: number().moreThan(0, "Hourly rate must be greater than 0"),
      otherwise: number().notRequired(),
    }),
    mileage_rate: number().when("type", {
      is: "investigator",
      then: number().moreThan(0, "Mileage rate must be greater than 0"),
      otherwise: number().notRequired(),
    }),
    database_rate: number().when("type", {
      is: "investigator",
      then: number().moreThan(0, "Database rate must be greater than 0"),
      otherwise: number().notRequired(),
    }),
    radius_miles: number().min(0, "Radius must be at least 0"),
    lat: number()
      .min(-90, "Latitude must be between -90 and 90")
      .max(90, "Latitude must be between -90 and 90"),
    lng: number()
      .min(-180, "Longitude must be between -180 and 180")
      .max(180, "Longitude must be between -180 and 180"),
  });

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initState}
      validationSchema={validateSchema}
      validateOnBlur={false}
      onSubmit={(values, { setSubmitting }) => {
        handleSubmit(values, setSubmitting);
      }}
    >
      {({ isSubmitting, values, errors, setFieldValue }) => (
        <Form
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <FormContainer>
            {/* Basic Info */}
            <FieldRow>
              <FieldGroup>
                <FieldLabel>Name *</FieldLabel>
                <StyledTextField
                  name="name"
                  value={values.name}
                  onChange={(e) => setFieldValue("name", e.target.value)}
                  disabled={initState.id !== ""}
                  size="small"
                  fullWidth
                  error={!!errors.name}
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Type</FieldLabel>
                <Select
                  value={fieldResourceTypes.find(
                    (opt) => opt.value === values.type,
                  )}
                  onChange={(e) => {
                    setFieldValue("type", e.value, false);
                    if (e.value === "processor") {
                      setFieldValue("hourly_rate", "", false);
                      setFieldValue("mileage_rate", "", false);
                      setFieldValue("database_rate", "", false);
                      if (!values.rate) setFieldValue("rate", "50", false);
                    } else {
                      setFieldValue("rate", "", false);
                      if (!values.hourly_rate)
                        setFieldValue("hourly_rate", "50", false);
                      if (!values.mileage_rate)
                        setFieldValue("mileage_rate", "0.65", false);
                      if (!values.database_rate)
                        setFieldValue("database_rate", "25", false);
                    }
                  }}
                  options={fieldResourceTypes}
                  styles={selectStyles}
                />
              </FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup>
                <FieldLabel>Email *</FieldLabel>
                <StyledTextField
                  name="email"
                  type="email"
                  value={values.email}
                  onChange={(e) => setFieldValue("email", e.target.value)}
                  size="small"
                  fullWidth
                  error={!!errors.email}
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Phone</FieldLabel>
                <InputMask
                  mask={getPhoneMask()}
                  value={values.phone}
                  onChange={(e) => setFieldValue("phone", e.target.value)}
                >
                  {(inputProps) => (
                    <StyledTextField {...inputProps} size="small" fullWidth />
                  )}
                </InputMask>
              </FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup style={{ gridColumn: "1 / -1" }}>
                <FieldLabel>Address</FieldLabel>
                <StyledTextField
                  name="address"
                  value={values.address}
                  onChange={(e) => setFieldValue("address", e.target.value)}
                  size="small"
                  fullWidth
                />
              </FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup>
                <FieldLabel>State</FieldLabel>
                <Select
                  value={
                    states.find((opt) => opt.value === values.state) || {
                      label: values.state,
                    }
                  }
                  onChange={(e) => setFieldValue("state", e.value, false)}
                  options={states}
                  styles={selectStyles}
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Radius (miles)</FieldLabel>
                <StyledTextField
                  name="radius_miles"
                  type="number"
                  value={values.radius_miles}
                  onChange={(e) =>
                    setFieldValue("radius_miles", e.target.value)
                  }
                  size="small"
                  fullWidth
                />
              </FieldGroup>
            </FieldRow>

            {/* Rates Section */}
            <SectionTitle>Rates</SectionTitle>
            {values.type === "processor" ? (
              <FieldRow>
                <FieldGroup>
                  <FieldLabel>Rate *</FieldLabel>
                  <StyledTextField
                    name="rate"
                    type="number"
                    value={values.rate}
                    onChange={(e) => setFieldValue("rate", e.target.value)}
                    size="small"
                    fullWidth
                    error={!!errors.rate}
                  />
                </FieldGroup>
                <FieldGroup>
                  <FieldLabel>Rating</FieldLabel>
                  <StyledTextField
                    name="average_rating"
                    type="number"
                    value={values.average_rating}
                    onChange={(e) =>
                      setFieldValue("average_rating", e.target.value)
                    }
                    size="small"
                    fullWidth
                  />
                </FieldGroup>
              </FieldRow>
            ) : (
              <>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>Hourly Rate *</FieldLabel>
                    <StyledTextField
                      name="hourly_rate"
                      type="number"
                      value={values.hourly_rate}
                      onChange={(e) =>
                        setFieldValue("hourly_rate", e.target.value)
                      }
                      size="small"
                      fullWidth
                      error={!!errors.hourly_rate}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>Mileage Rate *</FieldLabel>
                    <StyledTextField
                      name="mileage_rate"
                      type="number"
                      value={values.mileage_rate}
                      onChange={(e) =>
                        setFieldValue("mileage_rate", e.target.value)
                      }
                      size="small"
                      fullWidth
                      error={!!errors.mileage_rate}
                    />
                  </FieldGroup>
                </FieldRow>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>Database Rate *</FieldLabel>
                    <StyledTextField
                      name="database_rate"
                      type="number"
                      value={values.database_rate}
                      onChange={(e) =>
                        setFieldValue("database_rate", e.target.value)
                      }
                      size="small"
                      fullWidth
                      error={!!errors.database_rate}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>Rating</FieldLabel>
                    <StyledTextField
                      name="average_rating"
                      type="number"
                      value={values.average_rating}
                      onChange={(e) =>
                        setFieldValue("average_rating", e.target.value)
                      }
                      size="small"
                      fullWidth
                    />
                  </FieldGroup>
                </FieldRow>
              </>
            )}

            {/* Company Info Section */}
            <SectionTitle>Meta Info</SectionTitle>
            <FieldRow>
              <FieldGroup>
                <FieldLabel>Company Name</FieldLabel>
                <StyledTextField
                  name="meta.company_name"
                  value={values.meta?.company_name || ""}
                  onChange={(e) =>
                    setFieldValue("meta.company_name", e.target.value)
                  }
                  size="small"
                  fullWidth
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Contact Name</FieldLabel>
                <StyledTextField
                  name="meta.contact_name"
                  value={values.meta?.contact_name || ""}
                  onChange={(e) =>
                    setFieldValue("meta.contact_name", e.target.value)
                  }
                  size="small"
                  fullWidth
                />
              </FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup>
                <FieldLabel>County</FieldLabel>
                <StyledTextField
                  name="meta.county"
                  value={values.meta?.county || ""}
                  onChange={(e) => setFieldValue("meta.county", e.target.value)}
                  size="small"
                  fullWidth
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Coverage Scope</FieldLabel>
                <Select
                  value={
                    values.meta?.coverage_scope
                      ? coverageScopeOptions.find(
                          (opt) => opt.value === values.meta.coverage_scope,
                        )
                      : null
                  }
                  onChange={(e) =>
                    setFieldValue("meta.coverage_scope", e?.value || "", false)
                  }
                  options={coverageScopeOptions}
                  isClearable
                  placeholder="Select..."
                  styles={selectStyles}
                />
              </FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup>
                <FieldLabel>Website</FieldLabel>
                <StyledTextField
                  name="meta.website"
                  value={values.meta?.website || ""}
                  onChange={(e) =>
                    setFieldValue("meta.website", e.target.value)
                  }
                  size="small"
                  fullWidth
                  placeholder="https://example.com"
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>W-9 on File</FieldLabel>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.meta?.w9_on_file === "yes"}
                      onChange={(e) =>
                        setFieldValue(
                          "meta.w9_on_file",
                          e.target.checked ? "yes" : "no",
                          false,
                        )
                      }
                      size="small"
                      sx={{
                        color: tokens.color.brand.primaryAction,
                        "&.Mui-checked": { color: tokens.color.brand.primaryAction },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "0.875rem" }}>Yes</Typography>
                  }
                  sx={{ marginLeft: 0, marginTop: 0 }}
                />
              </FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup>
                <FieldLabel>Recommend</FieldLabel>
                <StyledTextField
                  name="meta.recommend"
                  value={values.meta?.recommend || ""}
                  onChange={(e) =>
                    setFieldValue("meta.recommend", e.target.value)
                  }
                  size="small"
                  fullWidth
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Notes</FieldLabel>
                <StyledTextField
                  name="meta.notes"
                  value={values.meta?.notes || ""}
                  onChange={(e) => setFieldValue("meta.notes", e.target.value)}
                  size="small"
                  fullWidth
                />
              </FieldGroup>
            </FieldRow>
          </FormContainer>

          {/* Footer */}
          <Box
            sx={{
              padding: "12px 16px",
              borderTop: `1px solid ${tokens.color.border.default}`,
              display: "flex",
              justifyContent: "flex-end",
              gap: "8px",
              backgroundColor: tokens.color.surface.default,
            }}
          >
            <PrimaryButton
              type="button"
              onClick={onClose}
              style={{
                backgroundColor: tokens.color.surface.sunken,
                color: tokens.color.text.primary,
                border: `1px solid ${tokens.color.border.default}`,
              }}
            >
              Cancel
            </PrimaryButton>
            <PrimaryButton type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? "Saving..."
                : initState.id === ""
                  ? "Add"
                  : "Update"}
            </PrimaryButton>
          </Box>
        </Form>
      )}
    </Formik>
  );
}

export default AddFieldResource;
